import type { ColumnsType } from "@similarweb/pro-logic-components";
import { colorsPalettes } from "@similarweb/styles";
import { Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import { abbrNumberFilter } from "app/filters/ngFilters";
import { NO_DATA_HYPHEN } from "app/pages/sales-intelligence/constants/common";
import ColumnsService from "app/pages/sales-intelligence/modules/company-analysis/services/columns/factory";
import type {
    AppsDigitalAssetRecord,
    WebsitesDigitalAssetRecord,
} from "app/pages/sales-intelligence/modules/company-analysis/types/digital-assets";

import { AppCell } from "../../../../components/shared/AppCell/AppCell";
import { SiteCell } from "../../../../components/shared/SiteCell/SiteCell";
import { VisitsCell } from "../cells/VisitsCell";
import { VisitsChangeCell } from "../cells/VisitsChangeCell";

const formatNumber = abbrNumberFilter();

export const getWebsitesDigitalAssetsColumns = (
    totalCount: number,
    totalRow: WebsitesDigitalAssetRecord | undefined,
): ColumnsType<WebsitesDigitalAssetRecord> => [
    {
        dataIndex: "domain",
        title: `${translate("si.company-analysis.digital-assets.table.columns.site")} ${totalCount ? ` (${totalCount})` : ""}`,
        sorter: ColumnsService.stringSorter("domain"),
        width: 176,
        children: [
            {
                dataIndex: "domain",
                key: "totalSite",
                title: () => (
                    <Text
                        fontVariant="listItem1"
                        color={colorsPalettes.carbon[400]}
                    >
                        {translate("si.company-analysis.digital-assets.table.columns.total")}
                    </Text>
                ),
                width: 176,
                render: (value: WebsitesDigitalAssetRecord["domain"]) => <SiteCell value={value} />,
            },
        ],
    },
    {
        dataIndex: "visits",
        title: translate("si.company-analysis.digital-assets.table.columns.visits"),
        sorter: ColumnsService.numberSorter("visits"),
        width: 210,
        children: [
            {
                dataIndex: "visits",
                key: "totalVisits",
                title: () => {
                    if (!totalRow) {
                        return "";
                    }

                    return (
                        <VisitsCell
                            visits={totalRow.visits}
                            visitsHistory={totalRow.visitsHistory}
                            isHeader
                        />
                    );
                },
                width: 210,
                render: (value: WebsitesDigitalAssetRecord["visits"], row) => (
                    <VisitsCell
                        visits={value}
                        visitsHistory={row.visitsHistory}
                    />
                ),
            },
        ],
    },
    {
        dataIndex: "monthlyVisitsChangeMom",
        title: translate("si.company-analysis.digital-assets.table.columns.monthlyVisitsChangeMom.card"),
        align: "right",
        sorter: ColumnsService.numberSorter("monthlyVisitsChangeMom"),
        width: 125,
        children: [
            {
                dataIndex: "monthlyVisitsChangeMom",
                key: "totalMonthlyVisitsChangeMom",
                title: () => {
                    if (!totalRow) {
                        return "";
                    }

                    return (
                        <VisitsChangeCell
                            value={totalRow.monthlyVisitsChangeMom}
                            isHeader
                        />
                    );
                },
                width: 125,
                render: (value: WebsitesDigitalAssetRecord["monthlyVisitsChangeMom"]) => (
                    <VisitsChangeCell value={value} />
                ),
            },
        ],
    },
];

export const getAppsColumns = (totalCount: number): ColumnsType<AppsDigitalAssetRecord> => [
    {
        dataIndex: "appName",
        title: `${translate("si.company-analysis.digital-assets.apps.table.columns.apps")}${totalCount ? ` (${totalCount})` : ""}`,
        sorter: ColumnsService.stringSorter("appName"),
        width: 176,
        render: (value: AppsDigitalAssetRecord["appName"], row) => <AppCell row={row}>{value}</AppCell>,
    },
    {
        dataIndex: "monthlyDownloads",
        title: translate("si.find_apps.table.column.title.monthly-downloads"),
        align: "right",
        sorter: ColumnsService.numberSorter("monthlyDownloads"),
        width: 125,
        render: (value: AppsDigitalAssetRecord["monthlyDownloads"]) =>
            value ? (formatNumber(value) as string) : NO_DATA_HYPHEN,
    },
    {
        dataIndex: "monthlyActiveUsers",
        title: translate("si.find_apps.table.column.title.monthly_active_users"),
        align: "right",
        sorter: ColumnsService.numberSorter("monthlyActiveUsers"),
        width: 125,
        render: (value: AppsDigitalAssetRecord["monthlyActiveUsers"]) =>
            value ? (formatNumber(value) as string) : NO_DATA_HYPHEN,
    },
];
